import { type AccessRequestView, CSRF_HEADER, type ProjectDetail } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { api } from "../../api.ts";
import { ok } from "../../api-errors.ts";
import { formatDate } from "../../i18n/format.ts";
import { useSession } from "../../session.tsx";
import { describeChange } from "../access-requests.ts";
import { can } from "../capabilities.ts";
import { projectKeys } from "../queries.ts";
import { SettingsSection } from "./SettingsSection.tsx";

export const accessRequestsKey = (projectId: string) =>
  [...projectKeys.project(projectId), "access-requests"] as const;

/**
 * Governance changes waiting for this project's approval (spec `project-settings` "Access
 * requests in settings"), for its owner and managers. Hidden when nothing waits. The requester's
 * own request carries no decision controls: the server refuses self-approval too.
 */
export function useAccessRequests(project: ProjectDetail) {
  const manages = can(project, "members.manage");
  return useQuery({
    queryKey: accessRequestsKey(project.id),
    queryFn: async (): Promise<AccessRequestView[]> =>
      ok(
        await api.api.projects[":projectId"]["access-requests"].$get({
          param: { projectId: project.id },
        }),
      ),
    enabled: manages,
    retry: false,
    select: (requests) => requests.filter((r) => r.status === "pending"),
  });
}

export function AccessRequestsSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const requests = useAccessRequests(project);
  const [outcome, setOutcome] = useState("");
  if (!requests.data || requests.data.length === 0) {
    // The outcome of the last decision stays announced after its row is gone.
    return (
      <p role="status" className="sr-only">
        {outcome}
      </p>
    );
  }
  return (
    <SettingsSection id="requests" level={3}>
      <p className="-mt-1 max-w-prose text-sm text-fg-secondary">
        {t("settings.accessRequests.lead")}
      </p>
      <ul
        data-esi-content="people"
        className="divide-y divide-line rounded-lg border border-line bg-surface-raised"
      >
        {requests.data.map((r) => (
          <RequestRow key={r.id} project={project} request={r} onDecided={setOutcome} />
        ))}
      </ul>
      <p role="status" className="text-sm text-fg-secondary">
        {outcome}
      </p>
    </SettingsSection>
  );
}

function RequestRow(props: {
  project: ProjectDetail;
  request: AccessRequestView;
  onDecided: (outcome: string) => void;
}) {
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const { request } = props;
  const name = request.requestedBy.displayName ?? request.requestedBy.email;
  const change = describeChange(request, t);
  const own = userId.toLowerCase() === request.requestedBy.userId.toLowerCase();
  const decide = useMutation({
    mutationFn: async (decision: "approve" | "reject") => {
      const route = api.api.projects[":projectId"]["access-requests"][":requestId"];
      const param = { projectId: props.project.id, requestId: request.id };
      const headers = { headers: { [CSRF_HEADER]: csrfToken } };
      return ok(
        decision === "approve"
          ? await route.approve.$post({ param }, headers)
          : await route.reject.$post({ param }, headers),
      );
    },
    onSuccess: (decided) => {
      props.onDecided(
        decided.status === "rejected"
          ? t("settings.accessRequests.rejected")
          : decided.group.approved === decided.group.size
            ? t("settings.accessRequests.applied")
            : t("settings.accessRequests.approved"),
      );
    },
    // The change may touch members, grants, settings or the project itself.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: projectKeys.project(props.project.id) }),
  });
  return (
    <li className="flex flex-col gap-3 px-4 py-3.5 sm:flex-row sm:items-start">
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <Avatar name={name} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-medium text-fg">{change}</span>
          <span className="text-xs text-fg-muted tabular-nums">
            {t("settings.accessRequests.requestedBy", {
              name,
              date: formatDate(request.createdAt),
            })}
            {" · "}
            {t("settings.accessRequests.expires", { date: formatDate(request.expiresAt) })}
            {request.group.size > 1
              ? ` · ${t("settings.accessRequests.group", request.group)}`
              : null}
          </span>
          {request.reason ? (
            <span className="text-sm text-fg-secondary">
              {t("settings.accessRequests.reason", { reason: request.reason })}
            </span>
          ) : null}
          {decide.error ? (
            <span className="text-sm text-danger-fg">{decide.error.message}</span>
          ) : null}
        </div>
      </div>
      {own ? (
        <span className="text-xs text-fg-muted">{t("settings.accessRequests.own")}</span>
      ) : (
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            aria-label={t("settings.accessRequests.rejectLabel", { change, name })}
            onClick={() => decide.mutate("reject")}
          >
            {t("settings.accessRequests.reject")}
          </Button>
          <Button
            size="sm"
            disabled={decide.isPending}
            aria-label={t("settings.accessRequests.approveLabel", { change, name })}
            onClick={() => decide.mutate("approve")}
          >
            {t("settings.accessRequests.approve")}
          </Button>
        </div>
      )}
    </li>
  );
}
